import type { ReactNode } from 'react'

import type {
  ProductionColumnId,
  ProductionOrderSummaryType,
} from '@aeris/contracts'
import { formatMoney } from '@aeris/money'
import { ZERO_AMOUNT, parseAmount } from '@aeris/sales'
import { Badge } from '@aeris/ui'

import {
  ART_STATUS_META,
  PAYMENT_MARK_META,
  STAGE_META,
  logisticsText,
} from './production-stage-meta.tsx'
import { documentRef } from './sales-labels.ts'
import type { ColumnRule } from './table-columns.ts'

/**
 * As colunas da fila de produção — F170.
 *
 * Na ordem da planilha "FILA DE PRODUÇÃO" da 4Play, no molde de
 * `stock-columns.tsx`: uma coluna nova é uma entrada aqui, e o id tem de
 * existir em `PRODUCTION_COLUMN_IDS`, que é o que o servidor aceita gravar.
 *
 * `filter` diz qual filtro de cabeçalho a coluna oferece; a tela é quem guarda
 * o valor e o manda para a consulta.
 */

export type ProductionFilterKey =
  | 'stage'
  | 'designer'
  | 'salesperson'
  | 'printer'
  | 'fabric'
  | 'logistics'
  | 'paymentMark'
  | 'artStatus'
  | 'dispatch'

export type ProductionColumn = ColumnRule<ProductionColumnId> & {
  readonly numeric?: boolean
  readonly className?: string
  readonly sortKey?:
    'dispatchDate' | 'number' | 'customer' | 'total' | 'stage' | 'pieces'
  readonly filter?: ProductionFilterKey
  readonly cell: (order: ProductionOrderSummaryType) => ReactNode
}

/** Quem nunca escolheu vê as colunas que a planilha tinha à vista. */
export const DEFAULT_PRODUCTION_COLUMNS: readonly ProductionColumnId[] = [
  'number',
  'customer',
  'city',
  'designer',
  'dispatchDate',
  'fabric',
  'personalized',
  'pieces',
  'total',
  'paymentMark',
  'salesperson',
  'printers',
  'artReady',
  'productionFileReady',
  'gradeChecked',
  'logistics',
  'artStatus',
  'stage',
]

const muted = (text = '—'): ReactNode => (
  <span className="text-(--text-muted)">{text}</span>
)

/** `AAAA-MM-DD` como a loja lê — sem passar por `Date`, que mudaria o dia no fuso. */
export const dayText = (day: string | null): string => {
  if (day === null) return ''
  const [year, month, date] = day.split('-')
  return `${date ?? ''}/${month ?? ''}/${year ?? ''}`
}

/** O texto decimal do fio, nunca um `Number()` (ADR-0016). */
export const moneyText = (value: string): string =>
  formatMoney(parseAmount(value) ?? ZERO_AMOUNT)

const money = (value: string | null): ReactNode =>
  value === null ? muted() : moneyText(value)

/** O "OK" da planilha: feito ou falta. */
const check = (done: boolean, missing: string): ReactNode =>
  done ? (
    <Badge tone="success">OK</Badge>
  ) : (
    <Badge tone="neutral">{missing}</Badge>
  )

export const PRODUCTION_COLUMNS: readonly ProductionColumn[] = [
  {
    id: 'number',
    label: 'Pedido',
    requires: [],
    locked: true,
    sortKey: 'number',
    className: 'font-(--font-mono) whitespace-nowrap',
    cell: (order) => documentRef('order', order.number),
  },
  {
    id: 'customer',
    label: 'Cliente',
    requires: [],
    locked: true,
    sortKey: 'customer',
    className: 'text-(--text-primary)',
    cell: (order) => order.customerName,
  },
  {
    id: 'city',
    label: 'Cidade',
    requires: [],
    className: 'text-(--text-secondary)',
    cell: (order) => order.customerCity ?? muted(),
  },
  {
    id: 'designer',
    label: 'Designer',
    requires: [],
    filter: 'designer',
    cell: (order) => order.designerName ?? muted(),
  },
  {
    id: 'dispatchDate',
    label: 'Despacho',
    requires: [],
    sortKey: 'dispatchDate',
    filter: 'dispatch',
    className: 'whitespace-nowrap',
    cell: (order) =>
      order.dispatchDate === null ? (
        muted('a definir')
      ) : order.late ? (
        <Badge tone="danger">{dayText(order.dispatchDate)}</Badge>
      ) : (
        dayText(order.dispatchDate)
      ),
  },
  {
    id: 'fabric',
    label: 'Tecido',
    requires: [],
    filter: 'fabric',
    cell: (order) => order.fabricName ?? muted('?'),
  },
  {
    id: 'personalized',
    label: 'Personalização',
    requires: [],
    cell: (order) =>
      order.personalized ? <Badge tone="info">Sim</Badge> : muted('Não'),
  },
  {
    id: 'pieces',
    label: 'Peças',
    requires: [],
    numeric: true,
    sortKey: 'pieces',
    cell: (order) => (order.pieces === 0 ? muted() : String(order.pieces)),
  },
  {
    id: 'total',
    label: 'Valor',
    // Quem só trabalha na arte e/ou na fábrica não vê dinheiro (F220).
    requires: ['sales.read'],
    numeric: true,
    sortKey: 'total',
    cell: (order) => money(order.totalAmount),
  },
  {
    id: 'paymentMark',
    label: 'Sinal',
    requires: ['sales.read'],
    filter: 'paymentMark',
    /*
     * O dinheiro de verdade (F173): o que entrou pelo caixa. A marcação da
     * planilha só aparece quando é Cortesia — ou quando nada foi pago e
     * alguém anotou "Sinal"/"100%" antes da F173, para a anotação antiga não
     * sumir sem explicação.
     */
    cell: (order) => {
      const paid = parseAmount(order.paidAmount ?? '0') ?? ZERO_AMOUNT
      const total = parseAmount(order.totalAmount ?? '0') ?? ZERO_AMOUNT
      if (order.paymentMark === 'cortesia') {
        return <Badge tone="neutral">Cortesia</Badge>
      }
      if (paid.units > 0n) {
        return paid.units >= total.units ? (
          <Badge tone="success">Pago</Badge>
        ) : (
          <Badge tone="warning">Sinal {money(order.paidAmount)}</Badge>
        )
      }
      return order.paymentMark === 'nao' ? (
        muted()
      ) : (
        <Badge tone={PAYMENT_MARK_META[order.paymentMark].tone}>
          {PAYMENT_MARK_META[order.paymentMark].label} (anotado)
        </Badge>
      )
    },
  },
  {
    id: 'ticket',
    label: 'Ticket/peça',
    requires: ['sales.read'],
    numeric: true,
    cell: (order) => money(order.ticketPerPiece),
  },
  {
    id: 'salesperson',
    label: 'Responsável',
    requires: [],
    filter: 'salesperson',
    cell: (order) => order.salespersonName ?? muted(),
  },
  {
    id: 'printers',
    label: 'Impressora',
    requires: [],
    filter: 'printer',
    cell: (order) =>
      order.printers.length === 0
        ? muted()
        : order.printers.map((printer) => printer.name).join(', '),
  },
  {
    id: 'artReady',
    label: 'Arte',
    requires: [],
    cell: (order) => check(order.artReady, 'Falta'),
  },
  {
    id: 'productionFileReady',
    label: 'Corel',
    requires: [],
    cell: (order) => check(order.productionFileReady, 'Falta'),
  },
  {
    id: 'gradeChecked',
    label: 'Grade',
    requires: [],
    cell: (order) =>
      order.gradeChecked ? (
        check(true, '')
      ) : order.pieces > 0 ? (
        <Badge tone="warning">Conferir</Badge>
      ) : (
        check(false, 'Falta')
      ),
  },
  {
    id: 'logistics',
    label: 'Logística',
    requires: [],
    filter: 'logistics',
    cell: (order) =>
      order.logistics.length === 0 ? muted() : logisticsText(order.logistics),
  },
  {
    id: 'event',
    label: 'Evento',
    requires: [],
    cell: (order) =>
      order.eventDate === null && order.eventNote === null
        ? muted()
        : [dayText(order.eventDate), order.eventNote ?? '']
            .filter((part) => part !== '')
            .join(' — '),
  },
  {
    id: 'notes',
    label: 'Observações',
    requires: [],
    className: 'max-w-64 truncate text-(--text-secondary)',
    cell: (order) => order.notes ?? muted(),
  },
  {
    // O andamento da arte (F171): é ele que libera a impressão.
    id: 'artStatus',
    label: 'Arte',
    requires: [],
    filter: 'artStatus',
    cell: (order) => (
      <Badge tone={ART_STATUS_META[order.artStatus].tone}>
        {ART_STATUS_META[order.artStatus].label}
      </Badge>
    ),
  },
  {
    id: 'stage',
    label: 'Etapa',
    requires: [],
    locked: true,
    sortKey: 'stage',
    filter: 'stage',
    cell: (order) => (
      <Badge
        tone={STAGE_META[order.stage].tone}
        icon={STAGE_META[order.stage].icon}
      >
        {STAGE_META[order.stage].label}
      </Badge>
    ),
  },
]
